import { useLazyQuery } from '@apollo/client';
import { USER_EXISTS_QUERY } from '../operations';
import { KEEP_ME_SIGN_IN_INPUT_NAME } from '../../Common/KeepMeSignedIn/KeepMeSignedInCheckbox';

/**
 * Custom hook for managing user existence checks in the join form workflow.
 *
 * This hook encapsulates the logic for checking if a user already exists with the provided
 * email address. Based on the result, it either enables step two of the form (new user) or
 * triggers the existing user flow (sign in).
 *
 * @param {Object} params - Hook configuration parameters
 * @param {Function} params.onIsExistingUser - Callback executed when user exists.
 *   Receives an object with { email, password, keepMeSignedIn } for sign-in flow
 * @param {Function} params.setIsStepTwoEnabled - State setter to enable/disable step two of the form
 * @param {Function} params.getValues - react-hook-form function to retrieve current form values
 * @param {Function} params.reset - react-hook-form function to reset form state while preserving values
 *
 * @returns {Object} Hook interface
 * @returns {Function} returns.checkUserExists - Function to trigger user existence check.
 *   Expects { variables: { email } } as parameter
 * @returns {boolean} returns.isUserExistsLoading - Loading state during user existence check
 * @returns {Error|null} returns.userExistsError - Error object if the check fails, null otherwise
 */
export const useUserExistsInForm = ({
  onIsExistingUser,
  setIsStepTwoEnabled,
  getValues,
  reset,
}) => {
  const [
    runUserExistsCheck,
    { loading: isUserExistsLoading, error: userExistsError },
  ] = useLazyQuery(
    USER_EXISTS_QUERY,
    {
      onCompleted: ({ isExistingUser }) => {
        if (!isExistingUser) {
          // User doesn't exist - enable step two and reset form state
          setIsStepTwoEnabled(true);

          // Reset the form state to clear isSubmitted flag, but keep all current values
          // This ensures validation state is reset while preserving user input
          const currentValues = getValues();
          reset(currentValues);

          return;
        }

        if (!onIsExistingUser) return;

        const formData = getValues();
        const { email, password } = formData;

        onIsExistingUser({
          email,
          password,
          keepMeSignedIn: formData[KEEP_ME_SIGN_IN_INPUT_NAME],
        });
      },
    },
  );

  return {
    checkUserExists: runUserExistsCheck,
    isUserExistsLoading,
    userExistsError,
  };
};
